import { Key } from 'antd/es/table/interface';
import { getNestedValue } from 'helpers/get-nested-value';

type Sorter<P> = (a: P, b: P) => number;
type SortingType = 'string' | 'number' | 'date' | 'boolean';
type Filter = (value: boolean | Key, property: string) => boolean;
type FilteringType = 'includes' | 'startsWith';

function tableSorterFactory<P>(sorter: Sorter<P>) {
  // getNestedValue is equivalent to lodash's get
  // as result the key can be a path to the nested value
  // eg. 'user.name' or 'user[0].name' or simple 'name'
  return <T>(key: string) =>
    (a: T, b: T) =>
      sorter(getNestedValue(a, key) as P, getNestedValue(b, key) as P);
}

function tableFilterFactory(filter: Filter) {
  // getNestedValue is equivalent to lodash's get
  // as result the key can be a path to the nested value
  // eg. 'user.name' or 'user[0].name' or simple 'name'
  return <T>(key: string) =>
    (value: boolean | Key, record: T) =>
      filter(value, getNestedValue(record, key) as string);
}
const sorters = {
  string: (a: string, b: string) => a.localeCompare(b),
  number: (a: number, b: number) => a - b,
  date: (a: Date, b: Date) => a.getTime() - b.getTime(),
  boolean: (a: boolean, b: boolean) => (a === b ? 0 : a ? 1 : -1),
} as Record<SortingType, Sorter<unknown>>;

// iterate over the sorters and wrap with high order function for each
export const tableSorters = Object.entries(sorters).reduce(
  (acc, [key, sorter]) => ({ ...acc, [key]: tableSorterFactory(sorter) }),
  {} as Record<keyof typeof sorters, ReturnType<typeof tableSorterFactory>>
);

const filters = {
  includes: (value, property) =>
    // not sure if needed, based on the antd types
    // value might be of type boolean / string / number / bigint
    typeof value === 'string' ? property.includes(value) : false,
  startsWith: (value, property) =>
    // not sure if needed, based on the antd types
    // value might be of type boolean / string / number / bigint
    typeof value === 'string' ? property.startsWith(value) : false,
} as Record<FilteringType, Filter>;

// iterate over the filters and wrap with high order function for each
export const tableFilters = Object.entries(filters).reduce(
  (acc, [key, sorter]) => ({ ...acc, [key]: tableFilterFactory(sorter) }),
  {} as Record<keyof typeof filters, ReturnType<typeof tableFilterFactory>>
);
